import { useCallback, useEffect, useRef } from 'react';
import { assetUrl } from '../api/client';
import { providerLabel } from '../lib/videoProvider';

/**
 * Accessible fullscreen media carousel for activity galleries.
 *
 * Built on the same focus-trap / keyboard / reduced-motion foundation as
 * `Lightbox`, but supports three slide types:
 *   - image: <img>
 *   - video: HTML5 <video controls> (uploaded mp4/webm)
 *   - embed: provider <iframe src={embed_url}> (YouTube / Facebook)
 *
 * Only the ACTIVE slide is mounted, which:
 *   - lazily mounts the iframe (never build iframe srcs ourselves — we use the
 *     server-provided `embed_url` verbatim per CSP), and
 *   - stops/pauses any playing <video> when the slide changes (the element
 *     unmounts on navigation).
 *
 * Props:
 *   - items: [{ type: 'image'|'video'|'embed', url, embed_url, alt_text, provider }]
 *   - index: active slide index (>= 0 to open, null/-1 to close)
 *   - onClose(): dismiss
 *   - onIndexChange(nextIndex): navigate (wrap-around)
 */
export default function MediaCarousel({ items = [], index, onClose, onIndexChange }) {
  const open = index != null && index >= 0 && index < items.length;
  const dialogRef = useRef(null);
  const closeRef = useRef(null);
  const restoreFocusRef = useRef(null);
  const videoRef = useRef(null);

  const go = useCallback(
    (delta) => {
      if (!items.length) return;
      const next = (index + delta + items.length) % items.length;
      onIndexChange?.(next);
    },
    [items.length, index, onIndexChange]
  );

  // Keyboard handling + focus trap while open (mirrors Lightbox).
  useEffect(() => {
    if (!open) return undefined;

    restoreFocusRef.current = document.activeElement;
    const focusTimer = window.setTimeout(() => closeRef.current?.focus(), 0);

    function onKeyDown(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose?.();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        go(1);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        go(-1);
      } else if (e.key === 'Tab') {
        const focusable = dialogRef.current?.querySelectorAll(
          'button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'
        );
        if (!focusable || focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    document.addEventListener('keydown', onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
      if (restoreFocusRef.current instanceof HTMLElement) {
        restoreFocusRef.current.focus();
      }
    };
  }, [open, go, onClose]);

  // Belt-and-suspenders: explicitly pause the video before the slide unmounts.
  useEffect(() => {
    const el = videoRef.current;
    return () => {
      if (el && !el.paused) el.pause();
    };
  }, [index]);

  if (!open) return null;

  const current = items[index];
  const multiple = items.length > 1;

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label="Media viewer"
      className="fixed inset-0 z-[60] flex items-center justify-center bg-maroon-950/90 p-4 animate-fade-in-slow sm:p-8"
      onClick={onClose}
    >
      <button
        ref={closeRef}
        type="button"
        onClick={onClose}
        aria-label="Close media viewer"
        className="absolute right-4 top-4 z-10 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-2xl text-white transition-colors hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-400"
      >
        &times;
      </button>

      {multiple && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            go(-1);
          }}
          aria-label="Previous item"
          className="absolute left-3 top-1/2 z-10 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-2xl text-white transition-colors hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-400 sm:left-6"
        >
          &#8249;
        </button>
      )}

      <figure
        className="flex max-h-full w-full max-w-5xl animate-scale-in flex-col items-center"
        onClick={(e) => e.stopPropagation()}
      >
        {current.type === 'image' && (
          <img
            src={assetUrl(current.url)}
            alt={current.alt_text || ''}
            className="max-h-[80vh] w-auto max-w-full rounded-lg object-contain shadow-lift"
          />
        )}

        {current.type === 'video' && (
          <video
            ref={videoRef}
            key={current.url}
            src={assetUrl(current.url)}
            controls
            autoPlay
            className="max-h-[80vh] w-auto max-w-full rounded-lg bg-black shadow-lift"
          >
            Your browser does not support embedded video.
          </video>
        )}

        {current.type === 'embed' && current.embed_url && (
          <div className="aspect-video w-full max-w-4xl overflow-hidden rounded-lg bg-black shadow-lift">
            {/* Mounted ONLY for the active slide (lazy). embed_url is used
                verbatim — the server builds a CSP-allowed provider URL. */}
            <iframe
              key={current.embed_url}
              src={current.embed_url}
              title={`${providerLabel(current.provider)} video`}
              className="h-full w-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              loading="lazy"
            />
          </div>
        )}

        <figcaption className="mt-3 flex items-center gap-3 text-sm text-cream-200">
          {current.alt_text && <span>{current.alt_text}</span>}
          {current.type !== 'image' && !current.alt_text && (
            <span className="capitalize">
              {current.type === 'embed' ? providerLabel(current.provider) : 'Video'}
            </span>
          )}
          {multiple && (
            <span aria-live="polite" className="text-cream-300">
              {index + 1} / {items.length}
            </span>
          )}
        </figcaption>
      </figure>

      {multiple && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            go(1);
          }}
          aria-label="Next item"
          className="absolute right-3 top-1/2 z-10 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-2xl text-white transition-colors hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-400 sm:right-6"
        >
          &#8250;
        </button>
      )}
    </div>
  );
}
